import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, Mail, Phone, ExternalLink, FileText, HelpCircle } from 'lucide-react';

const TABS = [
  ['help', 'Help Centre'], ['faqs', 'FAQs'], ['it', 'IT Support'],
  ['downloads', 'Downloads'], ['clinical', 'Clinical Resources'], ['forms', 'Forms & Templates'],
];

function Articles({ category }) {
  const [search, setSearch] = useState('');
  const [state, set] = useState({ items: null, error: null });
  const load = useCallback(async () => {
    try {
      const r = await api.get('/staff/resources/articles', { params: { category, search: search || undefined } });
      set({ items: r.data.data.items, error: null });
    } catch (e) {
      set({ items: [], error: e?.response?.status === 404 ? 'Resources are not enabled for your organisation yet.' : 'Failed to load' });
    }
  }, [category, search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-3">
      <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" data-testid="resources-search" aria-label="Search articles" />
      {state.items === null && <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>}
      {state.error && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="resources-error">{state.error}</div>}
      {state.items && !state.error && state.items.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground" data-testid="articles-empty">No articles found{search ? ' for your search' : ''}.</div>}
      {(state.items || []).map((a) => (
        <div key={a.id} className="border rounded-lg" data-testid={`article-${a.id}`}>
          <button className="w-full text-left px-3 py-2.5 text-sm font-medium flex items-center gap-2 focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
                  onClick={() => setOpen(open === a.id ? null : a.id)} aria-expanded={open === a.id} data-testid={`article-toggle-${a.id}`}>
            <HelpCircle className="h-4 w-4 text-primary flex-shrink-0" />{a.title}
          </button>
          {open === a.id && <div className="px-3 pb-3 text-sm text-muted-foreground whitespace-pre-wrap" data-testid={`article-body-${a.id}`}>{a.body}</div>}
        </div>
      ))}
    </div>
  );
}

function ITSupport() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get('/staff/resources/config').then((r) => setCfg(r.data.data)).catch(() => setCfg({})); }, []);
  if (!cfg) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  const none = !cfg.it_support_email && !cfg.it_support_phone && !cfg.it_support_portal_url;
  return (
    <div className="py-4 space-y-3 max-w-md" data-testid="it-support">
      {none && <p className="text-sm text-muted-foreground" data-testid="it-support-unconfigured">IT support contact details have not been configured for your organisation yet.</p>}
      {cfg.it_support_email && <Button asChild variant="outline" className="w-full justify-start" data-testid="it-support-email"><a href={`mailto:${cfg.it_support_email}`}><Mail className="h-4 w-4 mr-2" />{cfg.it_support_email}</a></Button>}
      {cfg.it_support_phone && <Button asChild variant="outline" className="w-full justify-start" data-testid="it-support-phone"><a href={`tel:${cfg.it_support_phone}`}><Phone className="h-4 w-4 mr-2" />{cfg.it_support_phone}</a></Button>}
      {cfg.it_support_portal_url && <Button asChild className="w-full justify-start" data-testid="it-support-portal"><a href={cfg.it_support_portal_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-2" />Open support portal</a></Button>}
      <p className="text-xs text-muted-foreground">Please don&apos;t include participant details in support requests.</p>
    </div>
  );
}

function DocList({ section, emptyLabel }) {
  const [state, set] = useState({ items: null, error: null });
  useEffect(() => {
    api.get('/staff/resources/documents', { params: { section } }).then((r) => {
      set({ items: r.data.data.items || [], error: null });
    }).catch((e) => {
      const st = e?.response?.status;
      set({ items: [], error: st === 404 ? 'Resources are not enabled for your organisation yet.' : st === 403 ? null : 'Unable to load resources' });
    });
  }, [section]);
  const open = async (doc) => {
    try {
      const r = await api.get(`/staff/resources/documents/${doc.id}/download-url`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.data.url}`, '_blank', 'noopener');
    } catch { toast.error('Unable to open this resource'); }
  };
  if (state.items === null) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  if (state.error) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="docs-error">{state.error}</div>;
  if (state.items.length === 0) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="docs-empty">{emptyLabel}</div>;
  return state.items.slice(0, 30).map((d) => (
    <div key={d.id} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`resource-doc-${d.id}`}>
      <FileText className="h-4 w-4 text-primary" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{d.title || d.file_name}</div>
        <div className="text-xs text-muted-foreground">{d.description || ''}{d.updated_at ? ` · updated ${new Date(d.updated_at).toLocaleDateString()}` : ''}</div>
      </div>
      <Button size="sm" variant="outline" className="h-7" onClick={() => open(d)} data-testid={`resource-doc-open-${d.id}`}>View</Button>
    </div>
  ));
}

export default function ResourcesPage() {
  const [tab, setTab] = useState('help');
  const content = {
    help: <Articles category="HELP_CENTRE" />,
    faqs: <Articles category="FAQS" />,
    it: <ITSupport />,
    downloads: <DocList section="DOWNLOADS" emptyLabel="No downloads published yet." />,
    clinical: <DocList section="CLINICAL" emptyLabel="No clinical resources published yet." />,
    forms: (
      <div className="space-y-2">
        <DocList section="FORMS" emptyLabel="No forms or templates published here yet." />
        <p className="text-xs text-muted-foreground">Policies remain available under <Link className="underline" to="/staff/policies">Staff Policies</Link>.</p>
      </div>
    ),
  };
  return (
    <div className="space-y-4" data-testid="resources-page">
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  aria-current={tab === k ? 'page' : undefined} data-testid={`resources-tab-${k}`}>{label}</button>
        ))}
      </div>
      <Card><CardContent className="py-3" data-testid="resources-content">{content[tab]}</CardContent></Card>
    </div>
  );
}
